/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  addYears,
  format,
  isAfter,
  isBefore,
  isValid,
  lastDayOfMonth,
  parse,
} from 'date-fns';
import { fr } from 'date-fns/locale/fr';
import queryString from 'query-string';
import { isNil, isNotNil, mapObjIndexed } from 'ramda';

import {
  Arguments,
  calculate,
  montantCoordination,
} from '@antistatique/cpef-calculator';

import { dateFields, defaultFields, fieldsList, numberFields } from './config';
import { queryParamsSchema } from './schemas';
import type { FormattedResults, Results, Store } from './types';
import { getAge } from './utils';

const calculator = {
  // Values
  fields: defaultFields,
  errors: {},
  results: null,
  resultsSansRachat: null,
  ageLPP: null,

  // Flags
  rachat: false,
  va: false,

  // Hydrate store with query params
  init() {
    // this.validate(fieldsList);

    const parsed = queryParamsSchema.safeParse(
      queryString.parse(window.location.search)
    );

    if (parsed.success) {
      this.fields = {
        ...this.fields,
        dateCertificat: parsed.data.date,
        dateNaissance: parsed.data.dob,
        salaireAVS: parsed.data.sal,
        txAct: isNotNil(parsed.data.rate) ? parsed.data.rate : undefined,
        choixDuPlan: isNotNil(parsed.data.plan)
          ? (parsed.data.plan.toLowerCase() as 'standard' | 'plus' | 'maxi')
          : undefined,
        avoirReg: parsed.data.fund,
        mesuresTrans: (parsed.data.trans ?? 0) + (parsed.data.transint ?? 0),
        dateRetraite: isNotNil(parsed.data.dob)
          ? lastDayOfMonth(addYears(parsed.data.dob, 65))
          : undefined,
      };

      this.ageLPP =
        getAge(this.fields.dateCertificat, this.fields.dateNaissance) ?? null;

      if (parsed.data.results) {
        this.calculate();
      }
    }
  },

  // Format date
  formatDate(date: Date, template = 'yyyy-MM-dd', isLastDayOfMonth = false) {
    return isNotNil(date) && isValid(date)
      ? format(isLastDayOfMonth ? lastDayOfMonth(date) : date, template, {
          locale: fr,
        })
      : '';
  },

  // Format number
  formatNumber(number: number) {
    if (isNil(number)) {
      return '';
    }

    return number.toLocaleString('en-CH', {
      minimumFractionDigits: number % 1 === 0 ? 0 : 2,
      maximumFractionDigits: 2,
    });
  },

  formatDelta(avec: number, sans: number) {
    const diff = avec - sans;

    if (diff === 0) {
      return '0';
    }

    const sign = diff > 0 ? '+' : '-';
    return sign + this.formatNumber(Math.abs(diff));
  },

  // Replace placeholders in titles
  formatTitle(title: string) {
    return isNotNil(this.results)
      ? title
          .replace(
            'DATE',
            this.formatDate(
              lastDayOfMonth(this.results.dateRetraite),
              'dd.MM.yyyy'
            )
          )
          .replace('AGE', Math.floor(this.results.ageRetraiteSaisie).toString())
          .replace(
            'MOIS',
            Math.round(
              (this.results.ageRetraiteSaisie -
                Math.floor(this.results.ageRetraiteSaisie)) *
                12
            ).toString()
          )
          .replace(
            'CAPITAL',
            this.results.projectionsSelonDate.capitalMaxRetraite
          )
      : title;
  },

  formatResults(results: ReturnType<typeof calculate>) {
    return mapObjIndexed((val, key) => {
      if (typeof val === 'number' && key !== 'ageRetraiteSaisie') {
        return this.formatNumber(val);
      }
      if (key === 'projectionsSelonDate') {
        return mapObjIndexed(
          (v, k) => {
            if (typeof v === 'number') {
              return this.formatNumber(v);
            }
            return v;
          },
          val as Results['projectionsSelonDate']
        );
      }
      if (key === 'projectionsSelonAgeEntier') {
        return mapObjIndexed(
          v =>
            mapObjIndexed(w => {
              if (typeof w === 'number') {
                return this.formatNumber(w);
              }
              return v;
            }, v),
          val as Results['projectionsSelonAgeEntier']
        );
      }
      return val;
    }, results);
  },

  buildResultsSansRachat(avec: Results, sans: Results) {
    const { projectionsSelonDate } = this.formatResults(sans);
    const avecP = avec.projectionsSelonDate as Results['projectionsSelonDate'] &
      Record<
        'renteRetraiteMensuelle' | 'renteRetraiteMensuelleAvecCapital',
        number
      >;
    const sansP = sans.projectionsSelonDate as typeof avecP;

    return {
      renteRetraiteAnnuelle: projectionsSelonDate.renteRetraiteAnnuelle,
      renteRetraiteMensuelle: projectionsSelonDate.renteRetraiteMensuelle,
      renteRetraiteAnnuelleAvecCapital:
        projectionsSelonDate.renteRetraiteAnnuelleAvecCapital,
      renteRetraiteMensuelleAvecCapital:
        projectionsSelonDate.renteRetraiteMensuelleAvecCapital,
      deltaAnnuel: this.formatDelta(
        avecP.renteRetraiteAnnuelle,
        sansP.renteRetraiteAnnuelle
      ),
      deltaMensuel: this.formatDelta(
        avecP.renteRetraiteMensuelle,
        sansP.renteRetraiteMensuelle
      ),
      deltaAnnuelAvecCapital: this.formatDelta(
        avecP.renteRetraiteAnnuelleAvecCapital,
        sansP.renteRetraiteAnnuelleAvecCapital
      ),
      deltaMensuelAvecCapital: this.formatDelta(
        avecP.renteRetraiteMensuelleAvecCapital,
        sansP.renteRetraiteMensuelleAvecCapital
      ),
    };
  },

  // Update field according to type
  updateField(field, value) {
    if (numberFields.includes(field)) {
      this.fields[field] = +value as any;
    } else if (dateFields.includes(field)) {
      this.fields[field] = parse(value, 'yyyy-MM-dd', new Date()) as any;
    } else {
      this.fields[field] = value;
    }

    if (field === 'dateNaissance' || field === 'dateCertificat') {
      this.ageLPP =
        getAge(this.fields.dateCertificat, this.fields.dateNaissance) ?? null;
    }

    this.validate(fieldsList);
  },

  toggleFlag(flag, value) {
    this[flag] = value ?? !this[flag];
    if (flag === 'rachat' && this.rachat === false) {
      this.resultsSansRachat = null;
    }
  },

  // Set default values, validate and calculate the results
  calculate() {
    this.resultsSansRachat = null;

    const fields = {
      ...this.fields,
      rachatMontant: this.rachat ? this.fields.rachatMontant : 0,
      vaMontant: this.va ? this.fields.vaMontant : 0,
      avoirReg: this.fields.avoirReg ?? 0,
      mesuresTrans: this.fields.mesuresTrans ?? 0,
      txAct: this.fields.txAct ? this.fields.txAct / 100 : 0,
    };

    this.validate(fieldsList);
    const formIsValid = Object.values(this.errors).every(isNil);

    if (formIsValid) {
      const results = calculate(fields as Arguments);
      this.results = this.formatResults(results);

      if (this.rachat) {
        const resultsSansRachat = calculate({
          ...fields,
          rachatMontant: 0,
        } as Arguments);
        this.resultsSansRachat = this.buildResultsSansRachat(
          results,
          resultsSansRachat
        );
      }
    }

    this.scrollToCalculator();
  },

  // Reset the store
  reset() {
    this.results = null;
    this.resultsSansRachat = null;
    this.rachat = false;
    this.va = false;
    this.scrollToCalculator();
  },

  scrollToCalculator() {
    const calculator = document.getElementById('calculator');
    if (calculator) {
      calculator.scrollIntoView({ behavior: 'smooth' });
    }
  },

  printResults() {
    document.body.classList.add('calculator-print');
    window.print();
    document.body.classList.remove('calculator-print');
  },

  // Validate fields
  validate(fields) {
    fields.forEach(field => {
      this.errors[field] = this.validators[field](this.fields);
    });
  },

  // Validators for each field
  validators: {
    avoirReg(fields) {
      if (isNotNil(fields.avoirReg) && fields.avoirReg < 0) {
        return 'negative';
      }
      return undefined;
    },

    choixDuPlan(fields) {
      if (isNil(fields.choixDuPlan)) {
        return 'required';
      }
      return undefined;
    },

    dateCertificat(fields) {
      const age = getAge(fields.dateCertificat, fields.dateNaissance);

      if (
        isNotNil(fields.dateCertificat) &&
        isBefore(new Date(fields.dateCertificat), new Date('2021-12-31'))
      ) {
        return 'after2021';
      }

      if (isNotNil(age) && age < 18) {
        return 'under18';
      }

      if (isNil(fields.dateCertificat)) {
        return 'required';
      }

      if (isNotNil(fields.dateCertificat) && !isValid(fields.dateCertificat)) {
        return 'not_valid';
      }

      return undefined;
    },

    dateNaissance(fields) {
      if (isNil(fields.dateNaissance)) {
        return 'required';
      }

      if (isNotNil(fields.dateNaissance) && !isValid(fields.dateNaissance)) {
        return 'not_valid';
      }

      return undefined;
    },

    dateRetraite(fields) {
      const age = getAge(fields.dateRetraite, fields.dateNaissance);

      if (isNil(fields.dateRetraite)) {
        return 'required';
      }

      if (isNotNil(fields.dateRetraite) && !isValid(fields.dateRetraite)) {
        return 'not_valid';
      }

      if (
        isNotNil(fields.dateCertificat) &&
        isBefore(new Date(fields.dateRetraite), new Date(fields.dateCertificat))
      ) {
        return 'beforeCertificat';
      }

      if (isNotNil(age) && age < 58) {
        return 'under58';
      }

      if (isNotNil(age) && age > 65 && fields.retraiteApres65 === false) {
        return 'above65';
      }

      if (isNotNil(age) && age > 70 && fields.retraiteApres65 === true) {
        return 'above70';
      }

      return undefined;
    },

    mesuresTrans(fields) {
      if (isNotNil(fields.mesuresTrans) && fields.mesuresTrans < 0) {
        return 'negative';
      }
      return undefined;
    },

    retraiteApres65(fields) {
      return undefined;
    },

    salaireAVS(fields) {
      if (isNil(fields.salaireAVS)) {
        return 'required';
      }

      if (fields.salaireAVS < 0) {
        return 'negative';
      }

      if (
        fields.salaireAVS - (montantCoordination * (fields.txAct ?? 0)) / 100 <
        0
      ) {
        return 'belowCoordination';
      }

      return undefined;
    },

    txAct(fields) {
      if (isNil(fields.txAct)) {
        return 'required';
      }

      if (fields.txAct < 0) {
        return 'negative';
      }

      if (fields.txAct > 100) {
        return 'above100';
      }

      return undefined;
    },

    txCrd(fields) {
      if (isNil(fields.txCrd)) {
        return 'required';
      }
      return undefined;
    },

    rachatDate(fields) {
      if (isNil(fields.rachatDate) && isNotNil(fields.rachatMontant)) {
        return 'required';
      }

      if (isNotNil(fields.rachatDate) && !isValid(fields.rachatDate)) {
        return 'not_valid';
      }

      if (
        isNotNil(fields.rachatDate) &&
        isNotNil(fields.dateCertificat) &&
        isBefore(new Date(fields.rachatDate), new Date(fields.dateCertificat))
      ) {
        return 'beforeCertificat';
      }

      if (
        isNotNil(fields.rachatDate) &&
        isNotNil(fields.dateRetraite) &&
        isAfter(new Date(fields.rachatDate), new Date(fields.dateRetraite))
      ) {
        return 'afterRetraite';
      }

      return undefined;
    },

    rachatMontant(fields) {
      if (isNil(fields.rachatMontant) && isNotNil(fields.rachatDate)) {
        return 'required';
      }

      if (isNotNil(fields.rachatMontant) && fields.rachatMontant < 0) {
        return 'negative';
      }

      return undefined;
    },

    vaDate(fields) {
      if (isNil(fields.vaDate) && isNotNil(fields.vaMontant)) {
        return 'required';
      }

      if (isNotNil(fields.vaDate) && !isValid(fields.vaDate)) {
        return 'not_valid';
      }

      if (
        isNotNil(fields.vaDate) &&
        isNotNil(fields.dateCertificat) &&
        isBefore(new Date(fields.vaDate), new Date(fields.dateCertificat))
      ) {
        return 'beforeCertificat';
      }

      if (
        isNotNil(fields.vaDate) &&
        isNotNil(fields.dateRetraite) &&
        isAfter(new Date(fields.vaDate), new Date(fields.dateRetraite))
      ) {
        return 'afterRetraite';
      }

      return undefined;
    },

    vaMontant(fields) {
      if (isNil(fields.vaMontant) && isNotNil(fields.vaDate)) {
        return 'required';
      }

      if (isNotNil(fields.vaMontant) && fields.vaMontant < 0) {
        return 'negative';
      }

      return undefined;
    },
  },
} as Store;

export default calculator;
